/**
 * Bar WINE LIST request + stored-block schemas (`/api/v1/bar-catalog/wine-list*`).
 * Rules: `packages/models/src/bar/BarWineList.ts`.
 */
import { z } from "zod";
import {
  BAR_WINE_SERVINGS,
  BAR_WINE_SHELF_LIFE_BY_WINE_MAX,
  BAR_WINE_SHELF_LIFE_DAYS_MAX,
  BAR_WINE_STYLES,
  BAR_WINE_POUR_OZ_MAX,
  BAR_WINE_PRICE_MAX,
  isValidBarWinePourOz,
  isValidBarWinePrice,
} from "@order-up/models";

const BarWinePourOzSchema = z
  .number()
  .refine(isValidBarWinePourOz, `A pour must be more than 0 and at most ${BAR_WINE_POUR_OZ_MAX} oz, to the hundredth.`);

const BarWinePriceSchema = z
  .number()
  .refine(isValidBarWinePrice, `A price must be between $0.00 and $${BAR_WINE_PRICE_MAX}, in whole cents.`);

const BarWineShelfLifeSchema = z
  .partialRecord(z.enum(BAR_WINE_STYLES), z.number().int().min(1).max(BAR_WINE_SHELF_LIFE_DAYS_MAX))
  .readonly();

const BarWineShelfLifeDaysSchema = z.number().int().min(1).max(BAR_WINE_SHELF_LIFE_DAYS_MAX);
const BarWineInventoryGuidSchema = z.string().min(1).max(128).refine((g) => !g.includes("/"), "Not an inventory id.");

/** Per wine (a preservation system): inventory guid → days. */
const BarWineShelfLifeByWineSchema = z
  .record(BarWineInventoryGuidSchema, BarWineShelfLifeDaysSchema)
  .refine((r) => Object.keys(r).length <= BAR_WINE_SHELF_LIFE_BY_WINE_MAX, `At most ${BAR_WINE_SHELF_LIFE_BY_WINE_MAX} wines can keep their own shelf life.`)
  .readonly();

/** `IModifier.barWine` — a generated wine serving option's own record of what it pours. */
export const BarWineOptionTagSchema = z.union([
  // A per-wine option (before 2026-10-16): it names its own wine.
  z
    .object({
      inventoryGuid: BarWineInventoryGuidSchema,
      serving: z.enum(BAR_WINE_SERVINGS),
      bottleOz: z.number().positive(),
      style: z.enum(BAR_WINE_STYLES),
    })
    .strict(),
  // A SHARED option (`bar_wine_serving_{serving}`): the serving only — the
  // wine is the line's item's `barWine` (design 2026-10-16).
  z.object({ serving: z.enum(BAR_WINE_SERVINGS) }).strict(),
]);

/** `IItem.barWine` — a listed wine's bottle, and the split a Glass of it is sold as. */
const BarWineBottleTagSchema = z
  .object({
    inventoryGuid: BarWineInventoryGuidSchema,
    bottleOz: z.number().positive(),
    style: z.enum(BAR_WINE_STYLES),
  })
  .strict();
export const BarWineItemTagSchema = BarWineBottleTagSchema.extend({ glassFrom: BarWineBottleTagSchema.optional() }).strict();

/**
 * `restaurant.settings.barWineList` as STORED: sparse, no nulls — "no large
 * pour" is the ABSENT key, never `null`. SERVER-WRITTEN only.
 */
export const LocationBarWineListBlockSchema = z
  .object({
    glassOz: BarWinePourOzSchema.optional(),
    largePourOz: BarWinePourOzSchema.optional(),
    /** Only `true` is stored — off is the absent key. */
    openBottleTracking: z.literal(true).optional(),
    /** Only the styles that differ from the standard shelf life. */
    shelfLifeDays: BarWineShelfLifeSchema.optional(),
    /** Only wines that keep their own shelf life. */
    shelfLifeDaysByWine: BarWineShelfLifeByWineSchema.optional(),
  })
  .strict();

/** `PUT /wine-list/settings` — `largePourOz: null` turns the large pour off. */
export const SaveBarWineSettingsSchema = z
  .object({
    glassOz: BarWinePourOzSchema.optional(),
    largePourOz: BarWinePourOzSchema.nullable().optional(),
    openBottleTracking: z.boolean().optional(),
    /** Per style; a style left out keeps what it has. */
    shelfLifeDays: BarWineShelfLifeSchema.optional(),
    /** Per wine: days sets its own shelf life, `null` returns it to its style's. A wine left out keeps what it has. */
    shelfLifeDaysByWine: z
      .record(BarWineInventoryGuidSchema, BarWineShelfLifeDaysSchema.nullable())
      .refine((r) => Object.keys(r).length <= 200, "Change at most 200 wines at once.")
      .optional(),
  })
  .strict()
  .refine((v) => Object.values(v).some((x) => x !== undefined), "Nothing to save.");
export type SaveBarWineSettingsInput = z.infer<typeof SaveBarWineSettingsSchema>;

/** `POST /wine-list/wines` — put carried wines on the list. */
export const AddBarWinesSchema = z
  .object({
    inventoryGuids: z.array(z.string().min(1).max(128)).min(1).max(200),
    /** Where the "Wine" menu group goes when it does not exist yet. */
    parentMenuGroupId: z.number().int().positive().optional(),
  })
  .strict();
export type AddBarWinesInput = z.infer<typeof AddBarWinesSchema>;

const BarWineRowPricesSchema = z
  .object({
    glass: BarWinePriceSchema.nullable().optional(),
    large: BarWinePriceSchema.nullable().optional(),
    bottle: BarWinePriceSchema.nullable().optional(),
    active: z.boolean().optional(),
  })
  .strict();

/** `PUT /wine-list/prices` — keyed by the wine's inventory guid. */
export const SaveBarWinePricesSchema = z
  .object({
    wines: z
      .record(z.string().min(1).max(128), BarWineRowPricesSchema)
      .refine((r) => Object.keys(r).length > 0 && Object.keys(r).length <= 200, "Save between 1 and 200 wines at once."),
  })
  .strict();
export type SaveBarWinePricesInput = z.infer<typeof SaveBarWinePricesSchema>;

/** `PUT /wine-list/open-bottles/:bottleId/remaining` — what the bar says is left in the bottle. */
export const SetBarOpenBottleRemainingSchema = z
  .object({
    remainingOz: z.number().min(0).max(200),
  })
  .strict();
export type SetBarOpenBottleRemainingInput = z.infer<typeof SetBarOpenBottleRemainingSchema>;

/**
 * `PUT /bar-catalog/wine-list/order` — the listed wines' inventory guids in the
 * order the till lays them out. Every listed wine exactly once; the service
 * refuses an order that has drifted from the list (`BAR_WINE_ORDER_STALE`).
 */
export const SaveBarWineOrderSchema = z
  .object({
    order: z.array(z.string().min(1).max(128)).min(1).max(500),
  })
  .strict();

export type SaveBarWineOrderInput = z.infer<typeof SaveBarWineOrderSchema>;

/**
 * `PUT /bar-catalog/wine-list/glass-from` — sell a wine's Glass as a whole
 * single-serve bottle of another carried wine (`splitGuid`), or pour it from
 * the wine's own bottle again (`null`). The service holds the rule
 * (`planBarWineGlassFrom`).
 */
export const SetBarWineGlassFromSchema = z
  .object({
    inventoryGuid: z.string().min(1).max(128),
    splitGuid: z.string().min(1).max(128).nullable(),
  })
  .strict();

export type SetBarWineGlassFromInput = z.infer<typeof SetBarWineGlassFromSchema>;
